import type { MouseEvent } from 'react';
import { DATE_FORMATS, formatUtcDateTimeToTimeZone } from '@belong/common';
import { IconSelfTourLockbox } from '@belong/icons';
import { LinkBase, Text } from '@belong/ui';
import { HOUSE_PAGE_STRINGS } from 'src/features/house-page/house-page.strings';
import { useSelfTourSlots } from '../../queries';
import { getSelfTourDayLabel } from '../../utils';

type SelfTourCardProps = {
  homeId: string;
  href: string;
  timeZone?: string;
  // Set when the visitor has to sign in before the booking flow opens.
  onSelect?: (href: string) => void;
};

const VISIBLE_SLOTS = 3;

export function SelfTourCard({
  homeId,
  href,
  timeZone,
  onSelect,
}: SelfTourCardProps) {
  const { data = [], isLoading } = useSelfTourSlots({ homeId });
  const slots = data.filter(({ isAvailable }) => isAvailable);
  const visibleSlots = slots.slice(0, VISIBLE_SLOTS);
  const moreCount = slots.length - visibleSlots.length;

  const now = Date.now();

  function linkProps(target: string) {
    return {
      href: target,
      onClick: onSelect
        ? (event: MouseEvent<HTMLAnchorElement>) => {
            event.preventDefault();
            onSelect(target);
          }
        : undefined,
    };
  }

  const header = (
    <div className="gap-sm flex items-center">
      <div className="md:w-icons-96x96 flex shrink-0 justify-center">
        <IconSelfTourLockbox width={60} className="text-navy" />
      </div>
      <div className="gap-2xs flex flex-col">
        <Text fontWeight="semibold">{HOUSE_PAGE_STRINGS['tour.selfTour']}</Text>
        <Text variant="p1" className="text-dark-gray">
          {HOUSE_PAGE_STRINGS['tour.selfTourDescription']}
        </Text>
      </div>
    </div>
  );

  if (!isLoading && !visibleSlots.length) {
    return (
      <LinkBase
        name="self_tour"
        {...linkProps(href)}
        className="p-sm duration-hover border-3 hover:border-navy block rounded border-solid border-transparent bg-white shadow hover:transition-colors"
      >
        {header}
      </LinkBase>
    );
  }

  return (
    <div className="p-sm gap-sm flex flex-col rounded bg-white shadow">
      {header}
      <div className="gap-xs grid grid-cols-3">
        {isLoading &&
          Array.from({ length: VISIBLE_SLOTS }, (_, index) => (
            <div
              key={index}
              aria-hidden
              className="p-xs bg-light-gray flex animate-pulse flex-col rounded border border-solid border-transparent"
            >
              <Text variant="p1" className="invisible">
                {HOUSE_PAGE_STRINGS['tour.selfTourToday']}
              </Text>
              <Text fontWeight="semibold" className="invisible">
                {HOUSE_PAGE_STRINGS['tour.selfTourToday']}
              </Text>
            </div>
          ))}
        {visibleSlots.map(({ startOn }) => (
          <LinkBase
            key={startOn}
            name="self_tour"
            {...linkProps(`${href}?startOn=${encodeURIComponent(startOn)}`)}
            className="p-xs border-gray hover:border-navy duration-hover flex flex-col items-center rounded border border-solid hover:transition-colors"
          >
            <Text variant="p1" className="text-dark-gray">
              {getSelfTourDayLabel(startOn, now, timeZone)}
            </Text>
            <Text fontWeight="semibold">
              {formatUtcDateTimeToTimeZone({
                dateTime: startOn,
                format: DATE_FORMATS.TIME,
                timeZone,
              })}
            </Text>
          </LinkBase>
        ))}
      </div>
      {isLoading && (
        <span aria-hidden className="invisible font-semibold">
          {HOUSE_PAGE_STRINGS['tour.selfTourOneMoreTime']}
        </span>
      )}
      {moreCount > 0 && (
        <LinkBase
          name="self_tour"
          {...linkProps(href)}
          className="text-navy self-center font-semibold underline"
        >
          {moreCount === 1
            ? HOUSE_PAGE_STRINGS['tour.selfTourOneMoreTime']
            : HOUSE_PAGE_STRINGS['tour.selfTourMoreTimes'].replace(
                '{count}',
                String(moreCount),
              )}
        </LinkBase>
      )}
    </div>
  );
}
